import React, { useEffect, useState } from "react";
import { getAPICall, putAPICall } from "lib/callApi";
import {
  FETCH_KPI_CATEGORIZATION,
  UPDATE_KPI_ACTIONS,
  UPDATE_KPI_SUB_CATEGORIES,
  FETCH_KPI_CATEGORIES,
} from "lib/constants";
import DataTable from "utils/Tables/DataTable";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { enqueueSnackbar } from "notistack";
import InputTextField from "./components/InputTextField";

function KPICategorization() {
  const [tableData, setTableData] = useState({
    columns: [
      {
        header: "COMPANY NAME",
        accessorKey: "companyName",
        wordBreak: "break-word",
        minWidth: "100px",
        id: 1,
      },
      {
        header: "KPI NAME",
        accessorKey: "kpiName",
        wordBreak: "break-word",
        minWidth: "100px",
        id: 2,
        enableColumnFilter: true,
        filterFn: "kpiNameFilter",
      },
      {
        header: "PRIMARY CATEGORY",
        accessorKey: "primaryCategory",
        wordBreak: "break-word",
        minWidth: "125px",
        id: 3,
      },
      {
        header: "SUB CATEGORY",
        accessorKey: "subCategory",
        wordBreak: "break-word",
        minWidth: "100px",
        id: 4,
      },
      {
        header: "SUB CATEGORY DEFINITION",
        accessorKey: "subCategoryDefinition",
        wordBreak: "break-word",
        minWidth: "250px",
        id: 5,
      },
      {
        header: "KPI MEDIUM ",
        accessorKey: "kpiMedium",
        wordBreak: "break-word",
        minWidth: "50px",
        id: 6,
      },
      {
        header: "ACTION TYPE",
        accessorKey: "actionType",
        wordBreak: "break-word",
        minWidth: "50px",
        id: 7,
      },
      {
        header: "CREATED AT",
        accessorKey: "createdAt",
        wordBreak: "break-word",
        minWidth: "100px",
        id: 8,
      },
    ],
    rows: [],
  });

  const [customKpisCount, setCustomKpisCount] = useState(0);
  const [incompleteCategoryCount, setIncompleteCategoryCount] = useState(0);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [reloadPage, setReloadPage] = useState(true);
  const [kpiCategories, setKpiCategories] = useState([]);
  const [kpiMediums, setKpiMediums] = useState([]);
  const [kpiActionTypes, setKpiActionTypes] = useState([]);
  const [tableRows, setTableRows] = useState([]);

  const updateKpiData = async ({ newValue, kpiActionId, columnToUpdate, subCategoryId }) => {
    try {
      let result = {};
      if (columnToUpdate === "primaryCategoryColumn") {
        const data = {
          kpiActionId,
          primaryCategoryId: newValue,
          columnToUpdate,
        };

        result = await putAPICall(`${UPDATE_KPI_ACTIONS}`, true, data);
      }

      if (columnToUpdate === "subCategoryColumn") {
        const data = {
          kpiActionId,
          subCategoryId: newValue,
          columnToUpdate,
        };

        result = await putAPICall(`${UPDATE_KPI_ACTIONS}`, true, data);
      }

      if (columnToUpdate === "kpiMediumColumn") {
        const data = {
          subCategoryId,
          kpiMedium: newValue,
          columnToUpdate,
        };

        result = await putAPICall(`${UPDATE_KPI_SUB_CATEGORIES}`, true, data);
      }

      if (columnToUpdate === "actionTypeColumn") {
        const data = {
          subCategoryId,
          actionType: newValue,
          columnToUpdate,
        };

        result = await putAPICall(`${UPDATE_KPI_SUB_CATEGORIES}`, true, data);
      }

      if (columnToUpdate === "definitionColumn") {
        const data = {
          subCategoryId,
          subCategoryDefinition: newValue,
          columnToUpdate,
        };

        result = await putAPICall(`${UPDATE_KPI_SUB_CATEGORIES}`, true, data);
      }

      if (result?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            // TODO: make this dynamic
            text: "Primary Category Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        setReloadPage((prev) => !prev);
      } else {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (reloadPage) {
      setReloadPage(false);
      (async () => {
        try {
          const kpisWithCategories = await getAPICall(FETCH_KPI_CATEGORIZATION, true);

          const rowsToSetWith = kpisWithCategories?.data?.data || [];
          const { customKpiCount, incompleteCategoryCountToSet } = rowsToSetWith.reduce(
            (counts, row) => {
              if (row.isCustom) counts.customKpiCount += 1;
              if (!row.primaryCategoryId || !row.subCategoryId)
                counts.incompleteCategoryCountToSet += 1;
              return counts;
            },
            {
              customKpiCount: 0,
              incompleteCategoryCountToSet: 0,
            }
          );

          setTableRows(rowsToSetWith);
          setCustomKpisCount(customKpiCount);
          setIncompleteCategoryCount(incompleteCategoryCountToSet);
        } catch (error) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: error?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      })();
    }
  }, [reloadPage]);

  useEffect(() => {
    (async () => {
      try {
        const kpiCategoriesResult = await getAPICall(FETCH_KPI_CATEGORIES, true);

        setKpiCategories(kpiCategoriesResult?.data?.data?.categoriesWithSubCategories || []);
        setKpiMediums(kpiCategoriesResult?.data?.data?.kpiMediums || []);
        setKpiActionTypes(kpiCategoriesResult?.data?.data?.kpiActionTypes || []);
      } catch (error) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: error?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!tableRows.length) return;

    const filteredTableRows = showIncomplete
      ? tableRows.filter((tableRow) => !tableRow.primaryCategoryId || !tableRow.subCategoryId)
      : tableRows;

    const preparedTableData = filteredTableRows.map((item) => {
      const subCategoriesToRender =
        kpiCategories.find((kpiPrimaryCategory) => kpiPrimaryCategory.id === item.primaryCategoryId)
          ?.KpiSubCategories || [];

      return {
        companyName: item.companyName,
        kpiName: item.kpiName,
        primaryCategory: (
          <Select
            value={item.primaryCategoryId}
            onChange={(e) =>
              updateKpiData({
                newValue: e.target.value,
                kpiActionId: item.kpiActionId,
                columnToUpdate: "primaryCategoryColumn",
              })
            }
            sx={{
              width: "150px",
            }}
          >
            {kpiCategories.map((kpiPrimaryCategory) => (
              <MenuItem key={kpiPrimaryCategory.id} value={kpiPrimaryCategory.id}>
                {kpiPrimaryCategory.primary_category_name}
              </MenuItem>
            ))}
          </Select>
        ),
        subCategory: (
          <Select
            value={item.subCategoryId}
            onChange={(e) =>
              updateKpiData({
                newValue: e.target.value,
                kpiActionId: item.kpiActionId,
                columnToUpdate: "subCategoryColumn",
              })
            }
            sx={{
              width: "150px",
            }}
          >
            {subCategoriesToRender.map((kpiSubCategory) => (
              <MenuItem key={kpiSubCategory.id} value={kpiSubCategory.id}>
                {kpiSubCategory.sub_category_name}
              </MenuItem>
            ))}
          </Select>
        ),
        subCategoryDefinition: (
          <InputTextField
            columnToUpdate="definitionColumn"
            subCategoryId={item.subCategoryId}
            subCategoryDefinition={item.subCategoryDefinition}
            updateKpiData={updateKpiData}
          />
        ),
        kpiMedium: (
          <Select
            value={item.kpiMedium}
            onChange={(e) =>
              updateKpiData({
                newValue: e.target.value,
                kpiActionId: item.kpiActionId,
                subCategoryId: item.subCategoryId,
                columnToUpdate: "kpiMediumColumn",
              })
            }
            sx={{
              width: "150px",
            }}
          >
            {kpiMediums.map((kpiMedium) => (
              <MenuItem key={kpiMedium.medium} value={kpiMedium.medium}>
                {kpiMedium.medium}
              </MenuItem>
            ))}
          </Select>
        ),
        actionType: (
          <Select
            value={item.actionType}
            onChange={(e) =>
              updateKpiData({
                newValue: e.target.value,
                kpiActionId: item.kpiActionId,
                subCategoryId: item.subCategoryId,
                columnToUpdate: "actionTypeColumn",
              })
            }
            sx={{
              width: "150px",
            }}
          >
            {kpiActionTypes.map((kpiActionType) => (
              <MenuItem key={kpiActionType.action_type} value={kpiActionType.action_type}>
                {kpiActionType.action_type}
              </MenuItem>
            ))}
          </Select>
        ),
        createdAt: <Typography variant="p">{item.createdAt}</Typography>,
      };
    });

    setTableData((prevState) => ({
      ...prevState,
      rows: preparedTableData,
    }));
  }, [tableRows, kpiCategories, kpiActionTypes, kpiMediums, showIncomplete]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card>
        <MDBox sx={{ ml: "auto", display: "flex", mt: 3, mr: 3 }}>
          <MDBox sx={{ mr: 2 }}>
            <label htmlFor="showIncomplete">
              <input
                id="showIncomplete"
                type="checkbox"
                checked={showIncomplete}
                onChange={(e) => setShowIncomplete(e.target.checked)}
                style={{ marginRight: "10px", size: "10px" }}
              />
              Show Incomplete
            </label>
          </MDBox>
          <MDBox sx={{ mr: 2 }}>
            Total Custom KPIs: <span style={{ color: "red" }}>{customKpisCount}</span>
          </MDBox>
          <MDBox>
            Incomplete: <span style={{ color: "red" }}>{incompleteCategoryCount}</span>
          </MDBox>
        </MDBox>
        <DataTable table={tableData} canSearch />
      </Card>
    </DashboardLayout>
  );
}

export default KPICategorization;
